import { useState, useEffect, useCallback, useMemo } from 'react';

/**
 * Reusable hook for managing checkbox-based bulk selection on paginated list pages.
 *
 * @param {Array} items - Current page items array. Each item must have an `id` property.
 * @returns {Object} Selection state and handler functions.
 */
const useBulkSelection = (items) => {
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Clear selection when items array reference changes (page nav, filter, customer change)
  useEffect(() => {
    setSelectedIds(new Set());
  }, [items]);

  const selectedCount = selectedIds.size;

  const isAllSelected = useMemo(
    () => items.length > 0 && selectedIds.size === items.length,
    [items, selectedIds]
  );

  const isIndeterminate = useMemo(
    () => selectedIds.size > 0 && selectedIds.size < items.length,
    [items, selectedIds]
  );

  const toggleItem = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      const allSelected = items.length > 0 && prev.size === items.length;
      if (allSelected) {
        return new Set();
      }
      return new Set(items.map((item) => item.id));
    });
  }, [items]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  return {
    selectedIds,
    isAllSelected,
    isIndeterminate,
    selectedCount,
    toggleItem,
    toggleAll,
    clearSelection,
  };
};

export default useBulkSelection;
